import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import StripeCheckout from '../Components/StripeCheckout';
import { useToast } from '../Components/ui/Toast.jsx';
import styles from './PaymentConfirmation.module.css';

const RESERVATION_DURATION_MS = 15 * 60 * 1000;

const PaymentConfirmation = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [timeLeft, setTimeLeft] = useState(RESERVATION_DURATION_MS);
    const [loading, setLoading] = useState(true);
    const [ticketData, setTicketData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [reference, setReference] = useState('');
    const [paymentCapture, setPaymentCapture] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('pending');

    const formatTime = (ms) => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleExpiration = useCallback(async () => {
        setPaymentStatus('expired');
        showToast('Tu tiempo de reserva ha expirado. Los asientos han sido liberados.', { type: 'warning' });
        navigate('/busroutes');
    }, [navigate, showToast]);

    useEffect(() => {
        if (!ticketId) {
            navigate('/busroutes');
            return;
        }

        const invoice = JSON.parse(localStorage.getItem('currentInvoice'));
        if (invoice && invoice.bookingId === Number(ticketId)) {
            setTicketData({
                id: invoice.bookingId,
                travelDetails: invoice.travelDetails,
                selectedSeats: invoice.selectedSeats,
                userDataPerSeat: invoice.userDataPerSeat,
                totalAmount: invoice.totalAmount
            });
            setLoading(false);

            const startTime = Date.now();
            const interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const remaining = RESERVATION_DURATION_MS - elapsed;

                if (remaining <= 0) {
                    clearInterval(interval);
                    handleExpiration();
                } else {
                    setTimeLeft(remaining);
                }
            }, 1000);

            return () => clearInterval(interval);
        } else {
            showToast('No se encontraron detalles de la reserva. Redirigiendo.', { type: 'warning' });
            navigate('/busroutes');
        }
    }, [ticketId, navigate, handleExpiration, showToast]);

    const handleSubmitManualPayment = async (e) => {
        e.preventDefault();

        if (!paymentCapture) {
            showToast('Debes adjuntar el comprobante de pago (captura).', { type: 'warning' });
            return;
        }

        if (!reference || reference.length < 4) {
            showToast('Por favor, ingresa los últimos 4 dígitos de la referencia de pago.', { type: 'warning' });
            return;
        }

        if (!paymentMethod) {
            showToast('Por favor, selecciona el método de pago utilizado.', { type: 'warning' });
            return;
        }

        const formData = new FormData();
        formData.append('ticket_id', ticketId);
        formData.append('payment_method', paymentMethod);
        formData.append('reference', reference);
        formData.append('capture', paymentCapture);

        setLoading(true);
        try {
            await api.post('/report_payment', formData);
            setPaymentStatus('submitted');
            showToast('¡Pago reportado con éxito! Recibirás la confirmación a tu correo después de la verificación.', { type: 'success' });
        } catch (error) {
            showToast('Ocurrió un error al cargar el comprobante. Intenta de nuevo.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleStripeSuccess = async (paymentIntentId) => {
        try {
            await api.post('/my_booking/stripe_confirm', {
                bookingId: ticketData.id,
                stripe_payment_intent: paymentIntentId,
            });
        } catch (err) {

        }
        setPaymentStatus('submitted');
    };

    const handleStripeError = (message) => {
        showToast(message, { type: 'error' });
    };

    if (loading) {
        return <div className={styles.container}>Cargando detalles de la reserva...</div>;
    }

    if (paymentStatus === 'submitted') {
        return (
            <div className={styles.statusPage}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIconSuccess}></div>
                    <h2 className={styles.successHeader}>Pago Confirmado</h2>
                    <p className={styles.statusText}>Tu pago fue procesado exitosamente. Recibiras el ticket por correo electronico.</p>
                    <p className={styles.statusText}>Código de reserva: <strong>#{ticketData.id}</strong></p>
                    <button className={styles.backButton} onClick={() => navigate('/busroutes')}>
                        Volver a Rutas
                    </button>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'expired') {
        return (
            <div className={styles.statusPage}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIconExpired}></div>
                    <h2 className={styles.expiredHeader}>Reserva Expirada</h2>
                    <p className={styles.statusText}>El tiempo para completar el pago ha terminado y los asientos han sido liberados.</p>
                    <button className={styles.backButton} onClick={() => navigate('/busroutes')}>
                        Buscar Nuevos Viajes
                    </button>
                </div>
            </div>
        );
    }

    const isManualMethod = paymentMethod === 'Zelle' || paymentMethod === 'PagoMovil' || paymentMethod === 'USDT';

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Confirma tu Pago</h2>
                <div className={styles.timerBlock}>
                    <p>Tiempo Restante:</p>
                    <span className={styles.timer}>{formatTime(timeLeft)}</span>
                </div>
            </div>

            <div className={styles.contentWrapper}>
                {/* Resumen de la reserva */}
                <div className={styles.detailsBlock}>
                    <h3>Detalle de la Reserva (#{ticketData.id})</h3>
                    <p><strong>Ruta:</strong> {ticketData.travelDetails.Origin} &rarr; {ticketData.travelDetails.Destination}</p>
                    <p><strong>Fecha:</strong> {new Date(ticketData.travelDetails.Departure_date).toLocaleDateString('es-ES')}</p>
                    <p><strong>Asientos:</strong> {ticketData.selectedSeats.join(', ')}</p>
                    <div className={styles.totalAmount}>
                        Total a Pagar: <span>${ticketData.totalAmount?.toFixed(2) || '0.00'} USD</span>
                    </div>
                </div>

                {/* Selector de método de pago */}
                <div className={styles.methodSelector}>
                    <h3>Selecciona el Método de Pago</h3>

                    <div className={styles.methodGrid}>
                        <button
                            className={`${styles.methodBtn} ${paymentMethod === 'Stripe' ? styles.methodBtnActive : ''}`}
                            onClick={() => setPaymentMethod('Stripe')}
                            type="button"
                        >
                            <span className={`${styles.methodIcon} ${styles.iconCard}`}></span>
                            <span className={styles.methodName}>Tarjeta</span>
                            <span className={styles.methodSub}>Visa / Mastercard</span>
                        </button>

                        <button
                            className={`${styles.methodBtn} ${paymentMethod === 'Zelle' ? styles.methodBtnActive : ''}`}
                            onClick={() => setPaymentMethod('Zelle')}
                            type="button"
                        >
                            <span className={`${styles.methodIcon} ${styles.iconBank}`}></span>
                            <span className={styles.methodName}>Zelle</span>
                            <span className={styles.methodSub}>USD</span>
                        </button>

                        <button
                            className={`${styles.methodBtn} ${paymentMethod === 'PagoMovil' ? styles.methodBtnActive : ''}`}
                            onClick={() => setPaymentMethod('PagoMovil')}
                            type="button"
                        >
                            <span className={`${styles.methodIcon} ${styles.iconMobile}`}></span>
                            <span className={styles.methodName}>Pago Móvil</span>
                            <span className={styles.methodSub}>VEF</span>
                        </button>

                        <button
                            className={`${styles.methodBtn} ${paymentMethod === 'USDT' ? styles.methodBtnActive : ''}`}
                            onClick={() => setPaymentMethod('USDT')}
                            type="button"
                        >
                            <span className={`${styles.methodIcon} ${styles.iconCrypto}`}></span>
                            <span className={styles.methodName}>USDT</span>
                            <span className={styles.methodSub}>TRC20</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Panel Stripe */}
            {paymentMethod === 'Stripe' && (
                <div className={styles.stripePanel}>
                    <StripeCheckout
                        amount={ticketData.totalAmount}
                        ticketId={ticketId}
                        onSuccess={handleStripeSuccess}
                        onError={handleStripeError}
                    />
                </div>
            )}

            {/* Instrucciones métodos manuales */}
            {isManualMethod && (
                <div className={styles.paymentInstructions}>
                    {paymentMethod === 'Zelle' && (
                        <div className={styles.methodCard}>
                            <h4>Pago por Zelle (USD)</h4>
                            <p><strong>Email:</strong> expresosflamingo@gmail.com</p>
                            <p><strong>Beneficiario:</strong> Expresos Flamingo C.A.</p>
                            <p className={styles.attention}>Coloca el código de reserva #{ticketData.id} como nota de pago.</p>
                        </div>
                    )}

                    {paymentMethod === 'PagoMovil' && (
                        <div className={styles.methodCard}>
                            <h4>Pago Móvil (VEF - Tasa BCV)</h4>
                            <p><strong>Banco:</strong> 0102 (Banco de Venezuela)</p>
                            <p><strong>Cédula:</strong> V-12.345.678</p>
                            <p><strong>Teléfono:</strong> 0412-1234567</p>
                            <p className={styles.attention}>El monto en VEF es calculado a la tasa actual del BCV.</p>
                        </div>
                    )}

                    {paymentMethod === 'USDT' && (
                        <div className={styles.methodCard}>
                            <h4>USDT (TRC20)</h4>
                            <p><strong>Wallet:</strong> TRC20_aBc1XyZ7PqR2S...</p>
                            <p className={styles.attention}>Solo se acepta la red TRC20. Transfiere el monto exacto.</p>
                        </div>
                    )}

                    {/* Formulario de comprobante */}
                    <form onSubmit={handleSubmitManualPayment} className={styles.reportForm}>
                        <h3>Reportar el Pago</h3>

                        <div className={styles.inputGroup}>
                            <label htmlFor="reference">Últimos 4 Dígitos de la Referencia:</label>
                            <input
                                id="reference"
                                type="text"
                                value={reference}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setReference(value);
                                }}
                                maxLength="4"
                                pattern="\d{4}"
                                title="Debe ser 4 dígitos numéricos"
                                required
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="capture">Adjuntar Comprobante (Capture / Foto):</label>
                            <input
                                id="capture"
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => setPaymentCapture(e.target.files[0])}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={loading || paymentStatus !== 'pending'}
                        >
                            {loading ? 'Enviando Comprobante...' : 'Enviar y Confirmar'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default PaymentConfirmation;
